import Store from 'store/store'
import { writable, derived } from 'utils/state'
import { Component } from 'utils/jsx'

import Button from 'components/dom/Button'
import Cover from 'components/dom/Cover'
import Export from 'components/dom/Export'
import Formats from 'components/dom/Formats'
import Landing from 'components/dom/Landing'
import Landscape from 'components/dom/Landscape'
import Logos from 'components/dom/Logos'
import Modal from 'components/dom/Modal'
import Papers from 'components/dom/Papers'
import Select from 'components/dom/Select'
import Step from 'components/dom/Step'
import TextEditable from 'components/dom/TextEditable'

import { save } from 'store/file'
import help from 'controllers/help'
import Browser from 'controllers/browser'

export default class App extends Component {
  beforeRender () {
    this.update = this.update.bind(this)
    this.prevStep = this.prevStep.bind(this)
    this.nextStep = this.nextStep.bind(this)
    this.latestStep = this.latestStep.bind(this)

    this.state = {
      steps: writable([]),
      showLanding: derived(Store.app.showLanding, v => window.ENV.features.landing && v)
    }
  }

  template () {
    return (
      <main
        id='App'
        class='app'
        store-class-has-guides={Store.app.showGuides}
        store-class-show-landing={this.state.showLanding}
        store-class-is-exporting={Store.app.isExporting}
        store-class-is-modified={Store.app.isModified}
        store-class-is-saving={Store.app.isSaving}
      >
        {window.ENV.features.landing && <Landing />}

        {window.ENV.features.regions && (
          <Step name='région' ref={this.refArray('steps')}>
            <Select label='Région concernée' store-options={Store.regions} />
          </Step>
        )}

        {(window.ENV.features.h1 || window.ENV.features.h2 || window.ENV.features.h3) && (
          <Step name='contenu texte' ref={this.refArray('steps')} validate={() => window.ENV.features.h1 ? !!Store.text.h1.current : true}>
            {window.ENV.features.h1 && (
              <TextEditable
                required
                class='lg'
                placeholder={window.ENV.labels.h1}
                store-value={Store.text.h1}
              />
            )}
            {window.ENV.features.h2 && (
              <TextEditable
                class='md'
                placeholder={window.ENV.labels.h2}
                store-value={Store.text.h2}
              />
            )}
            {window.ENV.features.h3 && (
              <TextEditable
                class='sm'
                placeholder={window.ENV.labels.h3}
                store-value={Store.text.h3}
                maxLength='240'
              />
            )}
            {window.ENV.features.h4 && (
              <TextEditable
                class='sm'
                placeholder={window.ENV.labels.h4}
                store-value={Store.text.h4}
                maxLength='240'
              />
            )}
            {window.ENV.features.h5 && (
              <TextEditable
                class='sm'
                placeholder={window.ENV.labels.h5}
                store-value={Store.text.h5}
                maxLength='240'
              />
            )}
            {window.ENV.features.h6 && (
              <TextEditable
                class='sm'
                placeholder={window.ENV.labels.h6}
                store-value={Store.text.h6}
                maxLength='240'
              />
            )}
          </Step>
        )}

        {window.ENV.features.landscape && (
          <Step name='paysage' ref={this.refArray('steps')}>
            <Landscape />
          </Step>
        )}

        {window.ENV.features.logos && (
          <Step name='logos partenaires' ref={this.refArray('steps')}>
            <Logos />
          </Step>
        )}

        {window.ENV.features.cover && (
          <Step name='image de couverture' ref={this.refArray('steps')}>
            <Cover />
          </Step>
        )}

        {window.ENV.features.formats && (
          <Step name='formats' ref={this.refArray('steps')} validate={() => Store.formats.current.find(f => f.selected)}>
            <Formats />
          </Step>
        )}

        <Step name='validation' ref={this.refArray('steps')}>
          <Papers />
        </Step>

        <Export />

        {window.ENV.features.buttons.help &&
          <Button class='app__help' icon='question' event-click={help} />}

        {!Browser.isSupported && (
          <Modal title='Navigateur non supporté'>
            <p>Votre navigateur <strong>{Browser.name} {Browser.version}</strong> n’est actuellement pas supporté.</p>
            <p>Vous pouvez fermer ce message et tenter d’utliser cet outil, mais certaines fonctionnalités peuvent ne pas fonctionner correctement.</p>
            <p>
              Navigateurs activement supportés:
              <ul>
                {Browser.supported.map(browser => <li>{browser.name} <b>{browser.version}</b></li>)}
              </ul>
            </p>
          </Modal>
        )}

        <footer class='app__footer'>
          <div class='flex-group'>
            {window.ENV.features.buttons.home &&
              <Button class='button--home' icon='home' event-click={() => window.location.reload()} title='Revenir à l’étape précédente' />}
            {window.ENV.features.buttons.save &&
              <Button class='button--save' icon='save' event-click={save} title='Enregistrer le projet' />}
            <Select store-value={Store.steps.index} store-options={this.state.steps} />
          </div>

          <div class='flex-group'>
            <Button class='button--prev' icon='angle-left' ref={this.ref('buttonPrev')} event-click={this.prevStep} title='Revenir à l’étape précédente' />
            <Button class='button--next' icon='angle-right' ref={this.ref('buttonNext')} event-click={this.nextStep} title='Aller à l’étape suivante'>suivant</Button>
            <Button class='button--latest' icon='angle-double-right' ref={this.ref('buttonLatest')} event-click={this.latestStep} title='Retourner à la dernière étape' />
            <Button
              class='button--export'
              icon='check'
              ref={this.ref('buttonExport')}
              store-disabled={Store.app.isRendering}
              event-click={() => Store.app.isExporting.set(true)}
            >
              télécharger
            </Button>
          </div>
        </footer>
      </main>
    )
  }

  afterRender () {
    Store.steps.length.set(this.refs.steps.length)

    const steps = this.refs.steps.map((step, index) => ({
      value: index,
      label: `Étape ${(index + 1)}: ${step.props.name}`,
      disabled: index > 0
    }))

    this.state.steps.set(steps)
  }

  afterMount () {
    Store.steps.index.subscribe(this.update)
    Store.steps.lastReached.subscribe(this.update)
    this.update()
  }

  get currentStep () {
    return this.refs.steps[Store.steps.index.get()]
  }

  prevStep () {
    if (Store.steps.index.get() === 0) return
    Store.steps.index.set(Store.steps.index.get() - 1)
  }

  nextStep () {
    if (window.ENV.production && !this.currentStep.validate()) return
    Store.steps.index.set(Store.steps.index.get() + 1)
  }

  latestStep () {
    if (window.ENV.production && !this.currentStep.validate()) return
    Store.steps.index.set(Store.steps.lastReached.get())
  }

  update () {
    const stepsIndex = Store.steps.index.get()
    const stepsLength = Store.steps.length.get()
    const lastReached = Store.steps.lastReached.get()
    const isLastStep = stepsIndex === (stepsLength - 1)

    // Enable relevant steps in <Select>
    this.state.steps.update(steps => {
      steps.forEach((step, index) => {
        step.selected = index === stepsIndex
        step.disabled = index > lastReached
      })
    }, true)

    // Reset isExporting app step, closing the <Export> screen
    Store.app.isExporting.set(false)

    // Show the right step
    this.refs.steps.forEach((step, index) => {
      step.state.hidden.set(index !== stepsIndex)
    })

    // Update progress-bar
    this.base.style.setProperty('--progress', stepsIndex / (this.refs.steps.length - 1))

    // Handle navigation bounds
    this.refs.buttonPrev.state.disabled.set(stepsIndex === 0)
    this.refs.buttonNext.state.disabled.set(stepsIndex >= this.refs.steps.length - 1)

    // Handle latest step visibility
    this.refs.buttonLatest.state.disabled.set(lastReached === 0 || lastReached <= stepsIndex)
    this.refs.buttonLatest.state.hidden.set(isLastStep)

    // Handle last step buttons visibility
    this.refs.buttonNext.state.hidden.set(isLastStep)
    this.refs.buttonExport.state.hidden.set(!isLastStep)
  }
}



// WEBPACK FOOTER //
// ./src/components/dom/App.js